import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { SettlementDetailsView } from '@lib/api-interface';
import { TableFilterItem } from '@lib/ui-components';
import { ExtractedItems } from '../libs/ui-components/components/infinite-scroll-renderer';
import { convertFiltersToParams } from '../libs/ui-components/components/data-table/data-table-filter/data-table-filter-utils';

/** The endpoint answers with this shape directly; it used to be assembled here out of the HAL page block. */
const extractItems = (response: AxiosResponse): ExtractedItems<SettlementDetailsView[]> => response.data as ExtractedItems<SettlementDetailsView[]>;

async function queryFn(params: any) {
  const response = await axios.get('/api/settlement/fetch-settlement-view-details-projections', { params });
  if (response.status === 200) {
    return extractItems(response);
  } else {
    throw new Error(response.data);
  }
}

async function queryAllFn(params: any, totalSettlementsCount?: number) {
  try {
    const promises = [];
    params.size = 1000;
    const totalPages = (totalSettlementsCount && Math.ceil(totalSettlementsCount / params.size)) || 1;
    for (let page = 0; page < totalPages; page++) {
      params.page = page;
      promises.push(axios.get('/api/settlement/fetch-settlement-view-details-projections', { params }).then((response) => extractItems(response)));
    }
    const pagesData = await Promise.all(promises);
    return pagesData.flatMap((page) => page.data ?? []);
  } catch (error) {
    throw new Error('An error occurred while fetching data', { cause: error });
  }
}

export const useSettlementDetailsByFilters = async (yearMonth: string, settlementStatus?: string, filters?: TableFilterItem[], totalSettlementsCount?: number) => {
  const params = {
    yearMonth,
    settlementStatus,
  };
  filters?.forEach((value) => Object.assign(params, { [value.column.key]: value.value }));

  return await queryAllFn(convertFiltersToParams(filters, params), totalSettlementsCount).then((response) => response);
};

export const useInfiniteSettlementDetailsByFilters = (
  yearMonth: string,
  settlementStatus?: string,
  filters?: TableFilterItem[],
  subcontractorCompanyId?: string,
  transporterId?: string,
): any => {
  const queryClient = useQueryClient();
  const queryKey = ['settlementViewDetailsInfinite', yearMonth, ...(settlementStatus !== undefined ? [settlementStatus] : []), filters];
  const params = {
    yearMonth,
    settlementStatus,
    subcontractorCompanyId,
    transporterId,
  };
  const invalidateQuery = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKey });
  };

  async function fetchSettlements({ pageParam = 0 }) {
    const queryParams = {
      ...convertFiltersToParams(filters, params),
      page: pageParam,
      size: 20,
    };
    return queryFn(queryParams);
  }

  return [
    useInfiniteQuery({
      queryKey,
      queryFn: fetchSettlements,
      initialPageParam: 0,
      getNextPageParam: (_) => _.currentPage + 1,
    }),
    invalidateQuery,
  ] as const;
};
